<template>
  <section>
    <notifications></notifications>

    <base-header>
      <div class="row align-items-center pt-header">
        <div class="col-lg-6 col-md-6 col-sm-6 col-6 d-flex align-items-center">
          <el-tooltip content="Back" placement="bottom">
            <base-button
              type="neutral"
              class="btn-icon-only"
              @click="goToPreviousPage()"
            >
              <i class="flaticon-left-1"></i>
            </base-button>
          </el-tooltip>

          <div>
            <h1 class="title-view">{{ originalEvent.name || "Event" }}</h1>
            <div class="box-id" v-if="loaded">
              <span class="number-id">{{ originalEvent.code }}</span>
              <span class="ml-2 mr-2">|</span>
              <div class="status-tab">
                <div
                  class="badge-status d-inline-flex align-items-center"
                  :class="getEventStatusClass(originalEvent.status)"
                >
                  {{ formatEventStatus(originalEvent.status) }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-lg-6 col-md-6 col-sm-6 col-6 text-right">
          <div class="box-buttons" v-if="loaded">
            <base-button
              type="outline-neutral"
              class="min-button no-mb ml-2"
              @click="showView()"
              v-if="mode === 'edit'"
            >
              <i class="flaticon-view-1"></i> View
            </base-button>

            <base-button
              type="outline-neutral"
              class="min-button no-mb ml-2"
              @click="showEdit()"
              v-if="mode === 'view'"
            >
              <i class="flaticon-pencil"></i> Edit
            </base-button>

            <base-button
              type="primary"
              class="min-button no-mb"
              @click="saveEvent()"
              v-if="mode === 'edit'"
            >
              <i class="flaticon-floppy-disk"></i> Save
            </base-button>
          </div>
        </div>
      </div>
    </base-header>

    <div class="container-fluid forms mb-5" v-if="loaded">
      <EventView v-if="mode === 'view'" :event="originalEvent" />

      <EventForm
        v-else
        :value="event"
        :v="$v.event"
        :customer-options="customerOptions"
      />
    </div>
  </section>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from "vuex";
import lodash from "lodash";
import { EventsAPI } from "@/core/api";
import { goBack, setSigOut } from "@/helpers/commons.helpers";
import EventForm from "../components/EventForm.vue";
import EventView from "../components/EventView.vue";
import {
  buildEmptyEventForm,
  buildEventFormFromEvent,
  buildUpdateEventPayload
} from "../services/event-form.service";
import {
  formatEventStatus,
  getEventStatusClass
} from "../services/event-format.service";
import { EVENT_FORM_VALIDATIONS } from "../services/event-form.validations";
import { getEventErrorMessage } from "../services/event-error.service";

const ERROR_MESSAGE = "The Operation couldn't be completed. Please try again";

export default {
  name: "EventDetails",
  data() {
    return {
      loaded: false,
      mode: "view",
      event: buildEmptyEventForm(),
      // Form as last loaded or saved, to send only the changed fields.
      originalForm: buildEmptyEventForm(),
      originalEvent: {}
    };
  },
  computed: {
    eventId() {
      return this.$route.params.id;
    },
    ...mapGetters(["sesion", "customerOptions"])
  },
  created() {
    if (!this.customerOptions.length) this.getCustomerOptions();
    this.getEvent();
  },
  methods: {
    formatEventStatus,
    getEventStatusClass,
    setBreadcrumb(name) {
      this.setItemsBread([
        { text: "Dashboard", to: "/dashboard" },
        { text: "Tools", to: "/tools" },
        { text: "Events", to: "/tools/events" },
        { text: name, active: true }
      ]);
    },
    loadEvent(event) {
      this.originalEvent = event;
      this.event = buildEventFormFromEvent(event);
      this.originalForm = lodash.cloneDeep(this.event);
      this.setBreadcrumb(event.name);
    },
    showEdit() {
      this.mode = "edit";
    },
    // Leaves edit mode discarding the unsaved changes.
    showView() {
      this.event = lodash.cloneDeep(this.originalForm);
      this.$v.event.$reset();
      this.mode = "view";
    },
    async getEvent() {
      try {
        this.setLoad(true);
        const { data } = await EventsAPI.getEventById(
          this.eventId,
          this.sesion.token
        );
        this.loadEvent(data);
        this.loaded = true;
      } catch (error) {
        this.handleError(error, ERROR_MESSAGE);
      } finally {
        this.setLoad(false);
      }
    },
    async saveEvent() {
      if (this.$v.event.$invalid) {
        this.$v.event.$touch();
        this.$notify({
          type: "warning",
          title: "Important!",
          message: "Please complete the required attributes",
          icon: "flaticon-warning-1"
        });
        return;
      }
      const payload = buildUpdateEventPayload(this.event, this.originalForm);
      if (!Object.keys(payload).length) {
        this.$notify({
          type: "info",
          title: "No changes",
          message: "There are no changes to save",
          icon: "flaticon-warning-1"
        });
        return;
      }
      await this.updateEvent(payload);
    },
    async updateEvent(payload) {
      try {
        this.setLoad(true);
        const { data } = await EventsAPI.updateEventById(
          this.eventId,
          payload,
          this.sesion.token
        );
        this.loadEvent({
          ...data,
          reservationsCount: this.originalEvent.reservationsCount
        });
        this.$v.event.$reset();
        this.mode = "view";
        this.$notify({
          type: "success",
          title: "Success!",
          message: "The Event has been Updated",
          icon: "flaticon-check-mark"
        });
      } catch (error) {
        this.handleError(error, ERROR_MESSAGE);
      } finally {
        this.setLoad(false);
      }
    },
    handleError(error, message) {
      if (error.response && error.response.data.code === 401) {
        if (setSigOut(error)) this.setSigOut(true);
        return;
      }
      this.$notify({
        type: "danger",
        title: "Error!",
        message: getEventErrorMessage(error, message),
        icon: "flaticon-remove"
      });
    },
    goToPreviousPage() {
      goBack(this);
    },
    ...mapMutations(["setSigOut", "setLoad", "setItemsBread"]),
    ...mapActions(["getCustomerOptions"])
  },
  validations: {
    event: EVENT_FORM_VALIDATIONS
  },
  components: {
    EventForm,
    EventView
  }
};
</script>
